/**
 * Standalone "create account" dialog.
 *
 * Mirrors the one-time setup wizard's account step (web/src/wizard/StepAccounts.tsx)
 * but as a modal that can be opened any time. In a single submit it:
 *   1. creates the account,
 *   2. saves its first raw alias exactly as it appears on the upline sheet,
 *   3. opens the first assignment (self-held, or a client with rates).
 *
 * Rates are entered as percents in the UI and stored as decimal multipliers
 * (1 = 100%). Negative and zero rates are allowed.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, similarFromError } from '../api';
import type { Account, AccountAlias, Assignment, Party, SimilarAccount } from '../api';
import {
  Button,
  Checkbox,
  ErrorBanner,
  Select,
  Spinner,
  TextInput,
} from '../ui/primitives';
import { Modal } from '../ui/Modal';

export interface NewAccountDialogProps {
  uplines: Party[];
  clients: Party[];
  onClose: () => void;
  onCreated: (account: Account) => void;
}

// Rates are stored as decimal multipliers (1 = 100%); the form takes percents.
function pctToRate(pct: string): string {
  const n = Number(pct);
  if (!Number.isFinite(n)) return pct.trim();
  return String(+(n / 100).toFixed(8));
}

// Today in Asia/Kolkata, formatted as YYYY-MM-DD for a native date input.
function todayInKolkata(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

export function NewAccountDialog({ uplines, clients, onClose, onCreated }: NewAccountDialogProps) {
  const qc = useQueryClient();
  const [uplinePartyId, setUplinePartyId] = useState('');
  const [canonicalName, setCanonicalName] = useState('');
  const [panel, setPanel] = useState('');
  const [creditLimitLabel, setCreditLimitLabel] = useState('');
  const [currencyLabel, setCurrencyLabel] = useState('INR');
  const [rawName, setRawName] = useState('');
  const [selfHeld, setSelfHeld] = useState(false);
  const [clientPartyId, setClientPartyId] = useState('');
  const [uplineRate, setUplineRate] = useState('');
  const [clientRate, setClientRate] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(todayInKolkata);
  const [defaultPointsSplit, setDefaultPointsSplit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Similar-name warning (Phase 4c). Warn only — "Create anyway" resubmits
  // with allowSimilar:true; nothing is ever auto-matched or blocked.
  const [similar, setSimilar] = useState<SimilarAccount[] | null>(null);

  const createMut = useMutation({
    mutationFn: async (allowSimilar: boolean) => {
      // 1. create the account
      const account = await api.post<Account>('/api/accounts', {
        uplinePartyId: Number(uplinePartyId),
        canonicalName: canonicalName.trim(),
        panel: panel.trim() || null,
        creditLimitLabel: creditLimitLabel.trim() || null,
        currencyLabel: currencyLabel.trim() || 'INR',
        allowSimilar,
      });
      // 2. save the first raw alias
      await api.post<AccountAlias>(`/api/accounts/${account.id}/aliases`, {
        rawName: rawName.trim(),
      });
      // 3. open the first assignment
      await api.post<Assignment>('/api/assignments', {
        accountId: account.id,
        clientPartyId: selfHeld ? null : Number(clientPartyId),
        clientRate: selfHeld ? null : pctToRate(clientRate),
        uplineRate: pctToRate(uplineRate),
        defaultPointsSplit,
        effectiveFrom,
      });
      return account;
    },
    onSuccess: (account) => {
      void qc.invalidateQueries();
      onCreated(account);
    },
    onError: (e) => {
      const matches = similarFromError<SimilarAccount>(e);
      if (matches) {
        setSimilar(matches);
        return;
      }
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSimilar(null);
    if (!uplinePartyId) {
      setError('Pick an upline.');
      return;
    }
    if (!canonicalName.trim()) {
      setError('Canonical account name is required.');
      return;
    }
    if (!rawName.trim()) {
      setError('First alias (raw upline name) is required.');
      return;
    }
    if (!selfHeld && !clientPartyId) {
      setError('Pick a client or mark the account self-held.');
      return;
    }
    if (!uplineRate.trim()) {
      setError('Upline rate is required.');
      return;
    }
    if (!selfHeld && !clientRate.trim()) {
      setError('Client rate is required.');
      return;
    }
    if (!effectiveFrom) {
      setError('Effective-from date is required.');
      return;
    }
    createMut.mutate(false);
  };

  return (
    <Modal onClose={onClose}>
        <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
          <h2 className="text-small font-semibold text-ink">New account</h2>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </header>
        <form className="flex-1 overflow-y-auto p-4" onSubmit={onSubmit}>
          <p className="mb-4 text-[13px] leading-5 text-ink-muted">
            This creates the account, saves the first raw alias exactly as it appears on the upline
            sheet, and opens the first assignment in one pass. Rates are percents and apply from the
            effective date forward — old entries keep their old rates.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Upline"
              value={uplinePartyId}
              onChange={setUplinePartyId}
              options={uplines.map((upline) => ({ value: String(upline.id), label: upline.displayName }))}
              placeholder="Choose..."
            />
            <TextInput
              label="Canonical name"
              value={canonicalName}
              onChange={(e) => setCanonicalName(e.target.value)}
              placeholder="e.g. Lotus365Aura"
              hint="Internal label shown throughout the app."
            />
            <TextInput
              label="Panel"
              value={panel}
              onChange={(e) => setPanel(e.target.value)}
              placeholder="e.g. SAURABJI-CLICK"
            />
            <TextInput
              label="Credit limit label"
              value={creditLimitLabel}
              onChange={(e) => setCreditLimitLabel(e.target.value)}
              placeholder="e.g. 50 KG or 2.2 CR"
            />
            <TextInput
              label="First alias (raw sheet name)"
              value={rawName}
              onChange={(e) => setRawName(e.target.value)}
              placeholder="e.g. S ADMIN (LOTUS365AURA)"
              hint="Case is preserved. Matching still stays case-insensitive."
            />
            <TextInput
              label="Currency label"
              value={currencyLabel}
              onChange={(e) => setCurrencyLabel(e.target.value)}
            />

            <div className="col-span-full rounded-[22px] border border-border/70 bg-surface-raised p-3">
              <div className="mb-2 text-small font-semibold tracking-[-0.01em] text-ink">Assignment</div>
              <Checkbox
                label="Self-held"
                hint="Turn this on if the account belongs to you directly, with no client and no client rate."
                checked={selfHeld}
                onChange={(value) => setSelfHeld(value)}
              />
            </div>

            {!selfHeld && (
              <Select
                label="Client"
                value={clientPartyId}
                onChange={setClientPartyId}
                options={clients.map((client) => ({ value: String(client.id), label: client.displayName }))}
                placeholder="Choose..."
              />
            )}
            {!selfHeld && (
              <TextInput
                label="Client rate (%)"
                value={clientRate}
                onChange={(e) => setClientRate(e.target.value)}
                placeholder="14"
                hint="Percent, e.g. 14 — negatives and zero allowed"
                inputMode="text"
              />
            )}
            <TextInput
              label="Upline rate (%)"
              value={uplineRate}
              onChange={(e) => setUplineRate(e.target.value)}
              placeholder="6"
              hint="Percent, e.g. 6 — negatives and zero allowed"
              inputMode="text"
            />
            <TextInput
              label="Effective from"
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              hint="Rates apply from this date forward; older entries keep their old values."
            />
            <div className="col-span-full">
              <Checkbox
                label="Split client points by default"
                hint="Leave this off for most accounts. Turn it on only when the client and upline points routinely differ."
                checked={defaultPointsSplit}
                onChange={(value) => setDefaultPointsSplit(value)}
              />
            </div>
          </div>

          {similar && similar.length > 0 && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="text-small font-medium text-amber-800">
                Similar {similar.length === 1 ? 'account' : 'accounts'} already{' '}
                {similar.length === 1 ? 'exists' : 'exist'} under this upline:
              </p>
              <ul className="mt-1 space-y-0.5 text-small text-amber-800">
                {similar.map((s) => (
                  <li key={s.id}>
                    <span className="font-semibold">{s.canonicalName}</span>
                    {s.panel ? ` — ${s.panel}` : ''}
                    {s.status !== 'active' ? ` (${s.status})` : ''}
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-caption text-amber-700">
                Same panel? If it&apos;s the same real account, close this and
                use it instead of creating a duplicate. Otherwise:
              </p>
              <div className="mt-2">
                <Button
                  type="button"
                  disabled={createMut.isPending}
                  onClick={() => {
                    setSimilar(null);
                    createMut.mutate(true);
                  }}
                >
                  {createMut.isPending ? <Spinner /> : 'Create anyway'}
                </Button>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-3">
              <ErrorBanner>{error}</ErrorBanner>
            </div>
          )}

          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? <Spinner /> : 'Create account'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
    </Modal>
  );
}
